import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";
import CityCombobox from "@/components/CityCombobox";
import { toast } from "sonner";
import { Hammer, HomeIcon, ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function ChooseRole() {
  const nav = useNavigate();
  const { user, setUser, refresh } = useAuth();
  const [step, setStep] = useState("pick"); // pick | pro-details
  const [busy, setBusy] = useState(false);
  const [trades, setTrades] = useState([]);
  const [city, setCity] = useState(null);
  const [form, setForm] = useState({
    role: null, trade: "", identifier_type: "siret", identifier: "",
    company: "", phone: "", accepted_charter: false, radius_km: 15,
  });

  useEffect(() => { api.get("/trades").then((r) => setTrades(r.data)); }, []);

  if (!user) return null;

  const chooseParticulier = async () => {
    if (!city) { toast.error("Merci de choisir votre ville"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/complete-role", {
        role: "particulier",
        city: city.name, dept: city.dept, lat: city.lat, lon: city.lon,
      });
      setUser(data.user);
      nav("/p/home", { replace: true });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitPro = async (e) => {
    e.preventDefault();
    const digits = (form.identifier || "").replace(/\D/g, "");
    const needed = form.identifier_type === "siren" ? 9 : 14;
    if (digits.length !== needed) { toast.error(`${form.identifier_type.toUpperCase()} : ${needed} chiffres requis`); return; }
    if (!form.trade) { toast.error("Métier requis"); return; }
    if (!city) { toast.error("Ville requise"); return; }
    if (!form.accepted_charter) { toast.error("Merci d'accepter la charte Pro"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/complete-role", {
        role: "pro", trade: form.trade, company: form.company, phone: form.phone,
        identifier_type: form.identifier_type, identifier: digits,
        city: city.name, dept: city.dept, lat: city.lat, lon: city.lon,
        radius_km: form.radius_km, accepted_charter: true,
      });
      setUser(data.user);
      nav("/pro/choose-plan", { replace: true });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (step === "pro-details") {
    return (
      <AppShell role="pro" title="Compléter le profil Pro" headerless={false}>
        <form onSubmit={submitPro} className="space-y-4">
          <div className="rounded-2xl bg-pro/10 border border-pro/20 p-3 text-xs text-pro">
            Connecté via Google — <b>{user.email}</b>. Complétez votre profil professionnel.
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate2">Entreprise (optionnel)</Label>
            <Input value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} className="rounded-xl h-12" placeholder="Ex: Plomberie Dupont" data-testid="cr-company-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate2">Téléphone</Label>
            <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="rounded-xl h-12" placeholder="0612345678" data-testid="cr-phone-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate2">Type d'identifiant</Label>
            <RadioGroup value={form.identifier_type} onValueChange={(v) => setForm((f) => ({ ...f, identifier_type: v }))} className="flex gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <RadioGroupItem value="siret" id="cr-siret" data-testid="cr-idtype-siret" />
                <span className="text-sm text-ink">SIRET</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <RadioGroupItem value="siren" id="cr-siren" data-testid="cr-idtype-siren" />
                <span className="text-sm text-ink">SIREN</span>
              </label>
            </RadioGroup>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate2">Numéro {form.identifier_type.toUpperCase()} *</Label>
            <Input inputMode="numeric" maxLength={form.identifier_type === "siren" ? 11 : 17} value={form.identifier} onChange={(e) => setForm((f) => ({ ...f, identifier: e.target.value }))} className="rounded-xl h-12 font-mono" placeholder={form.identifier_type === "siren" ? "123 456 789" : "123 456 789 00012"} data-testid="cr-identifier-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate2">Métier *</Label>
            <Select value={form.trade} onValueChange={(v) => setForm((f) => ({ ...f, trade: v }))}>
              <SelectTrigger className="rounded-xl h-12" data-testid="cr-trade-select"><SelectValue placeholder="Choisir un métier" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {trades.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate2">Ville *</Label>
            <CityCombobox value={city} onChange={setCity} testid="cr-city" />
          </div>
          <label className="flex items-start gap-3 p-3 bg-white rounded-2xl border border-border cursor-pointer">
            <Checkbox checked={form.accepted_charter} onCheckedChange={(v) => setForm((f) => ({ ...f, accepted_charter: !!v }))} data-testid="cr-accept-charter" />
            <span className="text-xs text-slate2">J&apos;accepte la charte Besoin d&apos;un Pro et les conditions de rémunération des demandes de contact.</span>
          </label>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-pro hover:bg-pro-hover text-white py-6 text-base font-semibold" data-testid="cr-submit-pro">
            {busy ? "Enregistrement…" : "Valider et activer mon compte Pro"}
          </Button>
        </form>
      </AppShell>
    );
  }

  return (
    <div className="app-shell grain-bg flex flex-col">
      <div className="flex-1 px-6 pt-10 pb-6">
        <h1 className="text-3xl font-heading font-bold text-ink leading-tight mb-2">
          Comment souhaitez-vous utiliser Besoin d&apos;un Pro&nbsp;?
        </h1>
        <p className="text-slate2 text-sm mb-8">Vous êtes connecté en tant que <b>{user.email}</b>.</p>

        <div className="space-y-4">
          <div className="w-full bg-white border border-border rounded-2xl p-5">
            <div className="flex items-start gap-4 mb-4">
              <div className="h-12 w-12 rounded-2xl bg-brand-light flex items-center justify-center">
                <HomeIcon className="w-6 h-6 text-brand" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-xl text-ink">Particulier</h3>
                <p className="text-xs text-slate2 mt-1.5">Je crée mes demandes de devis pour trouver un artisan local.</p>
              </div>
            </div>
            <div className="space-y-2 mb-3">
              <Label className="text-xs uppercase tracking-wider text-slate2">Votre ville *</Label>
              <CityCombobox value={city} onChange={setCity} testid="cr-city-part" />
            </div>
            <Button onClick={chooseParticulier} disabled={busy} className="w-full rounded-full bg-brand hover:bg-brand-hover text-white" data-testid="cr-pick-part-btn">
              Continuer en tant que particulier <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          <button
            onClick={() => setStep("pro-details")}
            className="w-full bg-pro text-white rounded-2xl p-5 text-left tap-scale"
            data-testid="cr-pick-pro-btn"
          >
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-white/10 flex items-center justify-center">
                <Hammer className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-xl">Professionnel</h3>
                <p className="text-xs text-white/70 mt-1.5">Je souhaite recevoir des demandes de devis locales et développer mon CA.</p>
                <div className="mt-3 text-xs text-white font-semibold flex items-center gap-1">Compléter mon profil Pro <ArrowRight className="w-4 h-4" /></div>
              </div>
            </div>
          </button>
        </div>

        <div className="mt-8 text-[11px] text-slate2 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-forest" /> Compte Google vérifié, email confirmé automatiquement.
        </div>
      </div>
    </div>
  );
}
